import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { AlertTriangle, ChevronLeft, ChevronRight, FileText, FlaskConical, Home, Leaf, Menu, Music, Network, Pill, Shield, Syringe, X } from "lucide-react";
import { LanguageToggle } from "./LanguageToggle";
import { Breadcrumbs } from "./Breadcrumbs";
import { WipBanner } from "./WipBanner";

type Section =
  | "home"
  | "blog"
  | "medcan"
  | "hr"
  | "matrix"
  | "benzo"
  | "opioide"
  | "terpene";

type NavItem = {
  to: string;
  label: string;
  icon: typeof Home;
  section: Section;
  keepInCompactNav?: boolean;
  emergency?: boolean;
  children?: NavItem[];
};

const NAV_ITEMS: NavItem[] = [
  { to: "/", label: "Home", icon: Home, section: "home" },
  { to: "/blog", label: "Blog", icon: FileText, section: "blog" },
  {
    to: "/medcan",
    label: "MedCanReview",
    icon: Leaf,
    section: "medcan",
    children: [
      { to: "/terpene", label: "Terpene", icon: FlaskConical, section: "terpene" },
    ],
  },
  {
    to: "/harm-reduction",
    label: "Harm Reduction",
    icon: Shield,
    section: "hr",
    children: [
      { to: "/matrix", label: "Interaktion", icon: Network, section: "matrix" },
      { to: "/benzo", label: "Benzodiazepine", icon: Pill, section: "benzo" },
      { to: "/opioide", label: "Opioide", icon: Syringe, section: "opioide" },
      { to: "/notfall", label: "Notfall", icon: AlertTriangle, section: "hr", keepInCompactNav: true, emergency: true },
    ],
  },
];



const SIDEBAR_FULL = 350;
const SIDEBAR_COLLAPSED = 64;
const COLLAPSED_KEY = "rl-sidebar-collapsed";

function NavLinks({
  onNavigate,
  tabIndex,
  firstLinkRef,
  compact = false,
}: {
  onNavigate?: () => void;
  tabIndex?: number;
  firstLinkRef?: React.Ref<HTMLAnchorElement>;
  compact?: boolean;
}) {
  const navRef = useRef<HTMLElement | null>(null);
  // Keep emergency access one click away while ordinary subpages stay collapsed.
  const visibleItems = compact
    ? NAV_ITEMS.flatMap((item) => [
        item,
        ...(item.children ?? []).filter((child) => child.keepInCompactNav),
      ])
    : NAV_ITEMS;

  function handleKeyDown(e: KeyboardEvent<HTMLElement>) {
    const root = navRef.current;
    if (!root) return;
    const links = Array.from(root.querySelectorAll<HTMLAnchorElement>("a.nav-link"));
    const idx = links.indexOf(document.activeElement as HTMLAnchorElement);
    if (idx === -1) return;
    let next = -1;
    if (e.key === "ArrowDown") next = (idx + 1) % links.length;
    else if (e.key === "ArrowUp") next = (idx - 1 + links.length) % links.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = links.length - 1;
    if (next !== -1) {
      e.preventDefault();
      links[next].focus();
    }
  }

  return (
    <nav
      ref={navRef}
      className="nav-links rl-hero-glass"
      aria-label="Hauptnavigation"
      onKeyDown={handleKeyDown}
      style={{ margin: "0.75rem", borderRadius: "var(--radius-md)", padding: "0.5rem" }}
    >
      {visibleItems.map(({ to, label, icon: Icon, section, emergency, children }, i) => {
        const baseClass = `nav-link${compact ? " nav-link-compact" : ""}${emergency ? " nav-link-emergency" : ""}`;
        const activeClass = `${baseClass} nav-link-active`;
        return (
          <div key={to} data-section={section}>
            <Link
              to={to}
              onClick={onNavigate}
              tabIndex={tabIndex}
              ref={i === 0 ? firstLinkRef : undefined}
              className={baseClass}
              aria-label={label}
              title={compact ? label : undefined}
              activeOptions={{ exact: to === "/" }}
              activeProps={{ className: activeClass, "aria-current": "page" }}
            >
              <Icon size={18} className="nav-link-icon" aria-hidden="true" />
              <span>{label}</span>
            </Link>
            {!compact && children && (
              <div className="nav-sublist">
                {children.map(({ to: subTo, label: subLabel, icon: SubIcon, section: subSection, emergency: subEmergency }) => {
                  const subBase = `nav-link nav-sublink${subEmergency ? " nav-link-emergency" : ""}`;
                  const subActive = `${subBase} nav-link-active`;
                  return (
                    <div key={subTo} data-section={subSection}>
                      <Link
                        to={subTo}
                        onClick={onNavigate}
                        tabIndex={tabIndex}
                        className={subBase}
                        aria-label={subLabel}
                        activeProps={{ className: subActive, "aria-current": "page" }}
                      >
                        <SubIcon size={14} className="nav-link-icon" aria-hidden="true" />
                        <span>{subLabel}</span>
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

    </nav>
  );
}



function LogoBlock({ compact = false }: { compact?: boolean }) {
  if (compact) {
    return (
      <div
        data-section="home"
        className="logo-block logo-block-compact"
      >
        <Link
          to="/"
          aria-label="Restlicht – zur Startseite"
          className="logo-link logo-link-icon"
        >
          <img
            className="logo-img"
            src="/logo.svg"
            alt=""
            aria-hidden="true"
            width={36}
            height={36}
            decoding="async"
            loading="eager"
          />
        </Link>
      </div>

    );
  }
  return (
    <div
      data-section="home"
      className="rl-hero-glass"
      style={{
        padding: "1.5rem 1.5rem 1rem",
        borderBottom: "1px solid var(--accent-border)",
        paddingBottom: "1rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        margin: "0.75rem 0.75rem 0",
        borderRadius: "var(--radius-md)",
      }}
    >
      <Link
        to="/"
        aria-label="Restlicht – zur Startseite"
        className="logo-link logo-link-full"
      >
        <img
          className="logo-img"
          src="/logo.svg"
          alt=""
          width={36}
          height={36}
          aria-hidden="true"
          decoding="async"
          loading="eager"
          fetchPriority="high"
        />

        <div
          style={{
            color: "var(--accent)",
            fontSize: "1.4rem",
            fontWeight: 800,
            whiteSpace: "nowrap",
            textShadow: "0 0 15px color-mix(in oklab, var(--accent) 60%, transparent)",
          }}
        >
          R E S T L I C H T . F Y I
        </div>
      </Link>
      <div style={{ color: "var(--text-secondary)", fontSize: "0.85rem", whiteSpace: "pre-line" }}>

        <span>Harm Reduction Informationen,</span>
        {"\n"}
        Cannabis-Reviews &{"\u00A0"}Blogs{"\n\n"}
      </div>
      <LanguageToggle />
    </div>
  );
}

function SidebarFooter() {
  return (
    <div
      className="rl-hero-glass rl-neon-frame"
      style={{
        padding: "0.6rem 0.75rem",
        marginLeft: "0.75rem",
        marginRight: "0.75rem",
        marginTop: "0.5rem",
        marginBottom: "0.75rem",
        borderRadius: "var(--radius-md)",
        color: "var(--text-muted)",
        fontSize: "0.75rem",
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        gap: "0.4rem",
        alignItems: "center",
      }}
    >
      <span>Stay safe. Stay informed.</span>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.4rem",
        }}
      >

        <a
          href="https://www.youtube.com/@NeonfxGraphics"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="YouTube"
          className="text-[var(--text-muted)] transition-colors duration-200 hover:text-[var(--accent)]"
          style={{
            width: 32,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "var(--radius-sm)",
          }}
        >
          <svg
            viewBox="0 0 24 24"
            width={18}
            height={18}
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
          </svg>
        </a>
        <a
          href="https://suno.com/@neonsfx"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Suno"
          className="text-[var(--text-muted)] transition-colors duration-200 hover:text-[var(--accent)]"
          style={{
            width: 32,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "var(--radius-sm)",
          }}
        >
          <Music size={18} aria-hidden="true" />
        </a>
        <a
          href="https://discord.gg/8hQgx2aA"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Discord"
          className="text-[var(--text-muted)] transition-colors duration-200 hover:text-[var(--accent)]"
          style={{
            width: 32,
            height: 32,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "var(--radius-sm)",
          }}
        >
          <svg
            viewBox="0 0 24 24"
            width={18}
            height={18}
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3602.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.082.082 0 00.0312-.0551c.5004-5.177-.8372-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1569 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1569 2.419 0 1.3332-.946 2.4189-2.1569 2.4189Z" />
          </svg>
        </a>
      </div>
      <a
        href="https://reimwerk.restlicht.fyi"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="ReimWerk (öffnet in neuem Tab)"
        className="text-[var(--text-muted)] transition-colors duration-200 hover:text-[var(--accent)] focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)] rounded-sm"
        style={{
          display: "block",
          width: "96px",
          textAlign: "center",
          fontSize: "0.7rem",
          textDecoration: "none",
          textTransform: "uppercase",
          letterSpacing: "0.18em",
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
          textShadow: "0 0 8px color-mix(in oklab, var(--accent) 55%, transparent)",
          padding: "0.25rem 0",
        }}
      >
        ReimWerk
      </a>
    </div>
  );
}

function useViewport(): "mobile" | "tablet" | "desktop" {
  const [vp, setVp] = useState<"mobile" | "tablet" | "desktop">("desktop");
  useEffect(() => {
    const mqMobile = window.matchMedia("(max-width: 767px)");
    const mqTablet = window.matchMedia("(min-width: 768px) and (max-width: 1023px)");
    const update = () => {
      if (mqMobile.matches) setVp("mobile");
      else if (mqTablet.matches) setVp("tablet");
      else setVp("desktop");
    };
    update();
    mqMobile.addEventListener("change", update);
    mqTablet.addEventListener("change", update);
    return () => {
      mqMobile.removeEventListener("change", update);
      mqTablet.removeEventListener("change", update);
    };
  }, []);
  return vp;
}

export function Layout({ children }: { children: ReactNode }) {
  const viewport = useViewport();
  const isMobile = viewport === "mobile";
  const isTablet = viewport === "tablet";
  const [open, setOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Embed view (?embed=1) is rendered inside foreign pages: no sidebar, no navigation.
  const isEmbed = useRouterState({
    select: (s) => String((s.location.search as Record<string, unknown> | undefined)?.["embed"] ?? "") === "1",
  });
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const firstNavLinkRef = useRef<HTMLAnchorElement | null>(null);

  // Tablet collapsed state
  const [collapsed, setCollapsed] = useState(false);
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const tabletAsideRef = useRef<HTMLElement | null>(null);
  

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = window.localStorage.getItem(COLLAPSED_KEY);
    if (stored === "1") setCollapsed(true);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent | globalThis.KeyboardEvent) => {
      if ((e as globalThis.KeyboardEvent).key === "Escape") {
        setOpen(false);
        toggleRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey as EventListener);
    const t = window.setTimeout(() => firstNavLinkRef.current?.focus(), 50);
    return () => {
      window.removeEventListener("keydown", onKey as EventListener);
      window.clearTimeout(t);
    };
  }, [open]);

  // Tablet pointer handlers for swipe collapse/expand (Chrome/Safari/iPadOS)
  const pointerRef = useRef<{ id: number; x: number; y: number; startCollapsed: boolean; axis: "?" | "x" | "y" } | null>(null);

  if (isEmbed) {
    return (
      <div style={{ minHeight: "100%", background: "var(--bg-primary)", position: "relative", isolation: "isolate" }}>
        <main id="main-content" tabIndex={-1} style={{ padding: "1.25rem", background: "transparent" }}>
          {children}
        </main>
      </div>
    );
  }

  function onTabletPointerDown(e: React.PointerEvent<HTMLElement>) {
    if (e.pointerType === "mouse") return;
    pointerRef.current = {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      startCollapsed: collapsed,
      axis: "?",
    };
  }
  function onTabletPointerMove(e: React.PointerEvent<HTMLElement>) {
    const drag = pointerRef.current;
    if (!drag || drag.id !== e.pointerId) return;
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    if (drag.axis === "?") {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      if (Math.abs(dx) > Math.abs(dy)) {
        drag.axis = "x";
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
      } else {
        drag.axis = "y";
        return;
      }
    }
    if (drag.axis !== "x") return;
    const base = drag.startCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_FULL;
    const next = Math.max(SIDEBAR_COLLAPSED, Math.min(SIDEBAR_FULL, base + dx));
    setDragWidth(next);
  }
  function endTabletPointer(e: React.PointerEvent<HTMLElement>, cancelled = false) {
    const drag = pointerRef.current;
    if (!drag || drag.id !== e.pointerId) return;
    pointerRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
    if (cancelled || drag.axis !== "x") {
      setDragWidth(null);
      return;
    }
    const w = dragWidth ?? (drag.startCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_FULL);
    const mid = (SIDEBAR_COLLAPSED + SIDEBAR_FULL) / 2;
    setCollapsed(w < mid);
    setDragWidth(null);
  }

  const skipLink = (
    <a href="#main-content" className="skip-link">
      Zum Hauptinhalt springen
    </a>
  );

  if (!isMobile) {
    const effectiveWidth = dragWidth ?? (collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_FULL);
    const isCompact = effectiveWidth < (SIDEBAR_COLLAPSED + SIDEBAR_FULL) / 2;
    return (
      <div style={{ minHeight: "100vh", background: "var(--bg-primary)", position: "relative", isolation: "isolate" }}>
        <div className="rl-fog-bg" aria-hidden="true" />
        {skipLink}
        <aside
          ref={tabletAsideRef}
          aria-label="Sidebar"
          aria-expanded={!isCompact}
          data-collapsed={isCompact ? "true" : "false"}
          className={isTablet ? "sidebar-tablet" : "sidebar-desktop"}
          onPointerDown={isTablet ? onTabletPointerDown : undefined}
          onPointerMove={isTablet ? onTabletPointerMove : undefined}
          onPointerUp={isTablet ? (e) => endTabletPointer(e, false) : undefined}
          onPointerCancel={isTablet ? (e) => endTabletPointer(e, true) : undefined}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: effectiveWidth,
            height: "100vh",
            zIndex: 40,
            background: "var(--bg-surface)",
            borderRight: "1px solid var(--border-purple)",
            display: "flex",
            flexDirection: "column",
            overflow: "visible",
            transition: dragWidth === null ? "width 0.24s cubic-bezier(0.22,0.61,0.36,1)" : "none",
            touchAction: "pan-y",
            WebkitTapHighlightColor: "transparent",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
            <LogoBlock compact={isCompact} />
            <NavLinks compact={isCompact} />
            {!isCompact && <SidebarFooter />}
          </div>
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            aria-label={isCompact ? "Sidebar ausklappen" : "Sidebar einklappen"}
            aria-expanded={!isCompact}
            className="sidebar-toggle"
          >
            {isCompact ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </aside>
        <main
          id="main-content"
          tabIndex={-1}
          style={{
            position: "relative",
            zIndex: 1,
            marginLeft: effectiveWidth,
            padding: "2rem",
            minHeight: "100vh",
            background: "transparent",
            transition: dragWidth === null ? "margin-left 0.24s cubic-bezier(0.22,0.61,0.36,1)" : "none",
          }}
        >
          <WipBanner />
          <Breadcrumbs />
          {children}
        </main>
      </div>
    );
  }


  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-primary)", position: "relative", isolation: "isolate" }}>
      <div className="rl-fog-bg" aria-hidden="true" />
      {skipLink}
      <header
        role="banner"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: 60,
          zIndex: 50,
          background: "var(--bg-surface)",
          borderBottom: "1px solid var(--border-purple)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 1rem",
        }}
      >
        <Link
          to="/"
          aria-label="Restlicht – zur Startseite"
          className="logo-link logo-link-icon logo-link-mobile"
        >
          <img
            className="logo-img"
            src="/logo.svg"
            alt=""
            aria-hidden="true"
            width={36}
            height={36}
            decoding="async"
            loading="eager"
            fetchPriority="high"
          />

        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Link
            to="/notfall"
            data-section="hr"
            className="nav-link nav-link-emergency"
            aria-label="Notfall"
            onClick={() => setOpen(false)}
            activeProps={{ className: "nav-link nav-link-emergency nav-link-active", "aria-current": "page" }}
            style={{ marginBottom: 0, minHeight: 44, minWidth: 44, padding: "0.5rem", gap: "0.5rem" }}
          >
            <AlertTriangle size={18} className="nav-link-icon" aria-hidden="true" />
            <span>Notfall</span>
          </Link>
          <LanguageToggle compact />
          <button
            ref={toggleRef}
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Menü schließen" : "Menü öffnen"}
            aria-expanded={open}
            aria-controls="mobile-sidebar"
            style={{
              background: "transparent",
              border: "none",
              color: "var(--text-primary)",
              cursor: "pointer",
              padding: 8,
            }}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <div
        onClick={() => setOpen(false)}
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 48,
          background: "rgba(0,0,0,0.5)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 0.25s ease",
        }}
      />

      <aside
        id="mobile-sidebar"
        role="dialog"
        aria-modal="true"
        aria-label="Hauptnavigation"
        {...(!open ? { inert: "" as unknown as boolean } : {})}
        style={{
          position: "fixed",
          top: 60,
          left: 0,
          bottom: 0,
          width: 280,
          zIndex: 49,
          background: "var(--bg-surface)",
          transform: open ? "translateX(0)" : "translateX(-100%)",
          visibility: open ? "visible" : "hidden",
          transition: open
            ? "transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1), visibility 0s"
            : "transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1), visibility 0s linear 0.28s",
          willChange: "transform",
          display: "flex",
          flexDirection: "column",
          borderRight: "1px solid var(--border-purple)",
          boxShadow: open ? "0 0 30px rgba(0,0,0,0.5)" : "none",
        }}
      >
        <NavLinks
          onNavigate={() => setOpen(false)}
          tabIndex={open ? 0 : -1}
          firstLinkRef={firstNavLinkRef}
        />
        <SidebarFooter />
      </aside>


      <main
        id="main-content"
        tabIndex={-1}
        style={{
          position: "relative",
          zIndex: 1,
          paddingTop: 60,
          padding: "60px 1rem 2rem",
          minHeight: "100vh",
          background: "transparent",
        }}
      >
        <WipBanner />
        <Breadcrumbs />
        {children}
      </main>
    </div>
  );
}
